import type { ReactNode } from "react";
import { z } from "zod";

// Safe Rate's own icon set for navigation and actions, drawn in the consumer
// site's StepIcon style (saferate-ai apps/consumer/app/components/home/
// StepIcon.tsx): 24px grid, stroke=currentColor, 1.75, round caps and joins.
// Each icon takes a className like a lucide icon, so it drops into the same
// places (sidebar menu buttons size it with [&>svg]:size-4).
const ZInputBrandIcon = z.object({ className: z.string().optional() });

type TInputBrandIcon = z.infer<typeof ZInputBrandIcon>;

export type TBrandIcon = (input: TInputBrandIcon) => ReactNode;

const makeIcon = (title: string, paths: ReactNode): TBrandIcon => {
	const Icon = (_input: TInputBrandIcon) => {
		const input = ZInputBrandIcon.parse(_input);
		return (
			<svg
				aria-hidden="true"
				className={input.className ?? "size-5"}
				fill="none"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.75}
				viewBox="0 0 24 24"
			>
				<title>{title}</title>
				{paths}
			</svg>
		);
	};
	return Icon;
};

/** The logo's idea: a house with a heart in it. */
export const IconHome = makeIcon(
	"Home",
	<>
		<path d="M3 11 12 3.5l9 7.5" />
		<path d="M5.5 9v11h13V9" />
		<path d="M12 18s-3.5-2-3.5-4.4A1.9 1.9 0 0 1 12 12.4a1.9 1.9 0 0 1 3.5 1.2C15.5 16 12 18 12 18Z" />
	</>,
);

/** A house bought with borrowed money: a house and a coin. */
export const IconLoans = makeIcon(
	"Loans",
	<>
		<path d="M2.5 11 10 4.5l7.5 6.5" />
		<path d="M4.5 9.5V19h6" />
		<circle cx="16.5" cy="16.5" r="4.5" />
		<path d="M16.5 14.25v4.5" />
	</>,
);

/** Money leaving: a coin with an arrow out. */
export const IconPayments = makeIcon(
	"Payments",
	<>
		<rect height="12" rx="2" width="17" x="2.5" y="7" />
		<path d="M2.5 11h17" />
		<path d="M6 15h3" />
		<path d="M17 4.5h4.5M19.5 2.5l2 2-2 2" />
	</>,
);

/** A monthly statement. */
export const IconStatements = makeIcon(
	"Statements",
	<>
		<path d="M7 3h7l4 4v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
		<path d="M14 3v4h4" />
		<path d="M8.5 11h7M8.5 14.5h7M8.5 18h4" />
	</>,
);

/** A folder holding a page. */
export const IconDocuments = makeIcon(
	"Documents",
	<>
		<path d="M3 8V6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8Z" />
		<path d="M3 10h18" />
		<path d="M9 14.5h6" />
	</>,
);

/** A payment that comes round again on its own. */
export const IconAutopay = makeIcon(
	"Autopay",
	<>
		<path d="M19.5 12a7.5 7.5 0 0 1-13 5.1" />
		<path d="M4.5 12a7.5 7.5 0 0 1 13-5.1" />
		<path d="M17.5 3.5v3.4h-3.4M6.5 20.5v-3.4h3.4" />
		<circle cx="12" cy="12" r="2" />
	</>,
);

/** A bank, drawn plainly. */
export const IconBank = makeIcon(
	"Bank accounts",
	<>
		<path d="M3 9.5 12 4l9 5.5" />
		<path d="M4 9.5h16" />
		<path d="M6 12.5v5M10 12.5v5M14 12.5v5M18 12.5v5" />
		<path d="M3.5 20.5h17" />
	</>,
);

/** People: the borrowers on the book. */
export const IconBorrowers = makeIcon(
	"Borrowers",
	<>
		<circle cx="9" cy="8.5" r="3" />
		<path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
		<path d="M15.5 5.75a3 3 0 0 1 0 5.5M17.5 14.25a5.5 5.5 0 0 1 3 5.25" />
	</>,
);

/** An overview: tiles of figures. */
export const IconDashboard = makeIcon(
	"Dashboard",
	<>
		<rect height="8" rx="2" width="7.5" x="3" y="3" />
		<rect height="5" rx="2" width="7.5" x="13.5" y="3" />
		<rect height="5" rx="2" width="7.5" x="3" y="16" />
		<rect height="8" rx="2" width="7.5" x="13.5" y="13" />
	</>,
);

/** Scheduled work: a clock with its run marked. */
export const IconJobs = makeIcon(
	"Jobs",
	<>
		<circle cx="12" cy="13" r="8" />
		<path d="M12 9v4l2.5 2" />
		<path d="M9.5 2.5h5" />
	</>,
);

/** Reports: bars over a baseline. */
export const IconReports = makeIcon(
	"Reports",
	<>
		<path d="M3 21h18" />
		<path d="M6 21V11M12 21V5M18 21v-7" />
	</>,
);

/** Performance: a line that rises. */
export const IconPerformance = makeIcon(
	"Performance",
	<>
		<path d="M3 21h18" />
		<path d="m4 16 5-5 3.5 3.5L20 7" />
		<path d="M15.5 7H20v4.5" />
	</>,
);

/** Portfolio: a pie with one slice out. */
export const IconPortfolio = makeIcon(
	"Portfolio",
	<>
		<path d="M11 4.05A8 8 0 1 0 19.95 13H11V4.05Z" />
		<path d="M14 3a7 7 0 0 1 7 7h-7V3Z" />
	</>,
);
